import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  MessageCircle, X, ArrowLeft, Send, Image as ImageIcon, Search, Users,
  Loader2, Check, CheckCheck, Paperclip, ChevronDown, Circle, Minimize2, Smile,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Palette Élégance Africaine
const PALETTE = {
  forest: '#2B3A2C',
  forestSoft: '#3E4F3F',
  copper: '#B85C38',
  sand: '#F5EFE1',
  bubbleMine: '#DFF3D9', // vert très clair (moi)
  bubbleTheirs: '#FFFFFF', // blanc (autres)
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?';

// Avatar coloré (couleur dérivée du nom)
const Avatar = ({ name, size = 36, online }) => {
  const hue = useMemo(() => {
    let h = 0;
    (name || '').split('').forEach((c) => { h = (h * 31 + c.charCodeAt(0)) % 360; });
    return h;
  }, [name]);
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <div
        className="w-full h-full rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm"
        style={{ background: `linear-gradient(135deg, hsl(${hue}, 55%, 42%), hsl(${(hue + 40) % 360}, 60%, 32%))` }}
      >
        {initials(name)}
      </div>
      {online !== undefined && (
        <span
          className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? 'bg-emerald-500' : 'bg-gray-300'}`}
        />
      )}
    </div>
  );
};

const CHAT_ROLES = ['agent', 'admin', 'super_admin'];

// Emojis groupés par catégorie — pas de dépendance externe
const EMOJI_CATEGORIES = [
  {
    key: 'recent',
    label: 'Fréquents',
    icon: '⭐',
    emojis: ['😀', '😂', '❤️', '👍', '🙏', '🎉', '🔥', '👌', '✨', '💯'],
  },
  {
    key: 'smileys',
    label: 'Émotions',
    icon: '😀',
    emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','😋','😛','😜','🤪','😝','🤗','🤭','🤫','🤔','🤐','😐','😑','😶','😏','😒','🙄','😬','😮','😯','😲','😳','🥺','😢','😭','😤','😠','😡','🥳','🥵','🥶','😱','😨','😰','😥','😓'],
  },
  {
    key: 'gestures',
    label: 'Gestes',
    icon: '👍',
    emojis: ['👍','👎','👌','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','👇','☝️','✋','🤚','🖐️','🖖','👋','🤝','🙏','💪','🫡','🫰','🫶','🤲','👏','🙌','👐','🤦','🤷'],
  },
  {
    key: 'hearts',
    label: 'Cœurs',
    icon: '❤️',
    emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟'],
  },
  {
    key: 'office',
    label: 'Bureau',
    icon: '💼',
    emojis: ['💼','📁','📂','📊','📈','📉','📋','📝','✏️','📌','📎','🗓️','📅','📆','⏰','⌛','⏳','💰','💵','💴','💶','💷','💳','🧾','🏢','🏠','🏘️','🔑','🗝️','🚗','📱','💻','🖥️','⌨️','🖨️','📞','☎️','📧','📨','📬'],
  },
  {
    key: 'symbols',
    label: 'Symboles',
    icon: '✅',
    emojis: ['✅','❌','⚠️','❗','❓','⁉️','‼️','💯','🔥','⭐','🌟','✨','💫','🎉','🎊','🎯','🚀','⚡','🔔','🔕','🏆','🥇','🥈','🥉','🎁','🎈'],
  },
];

const FloatingChat = () => {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [view, setView] = useState('list'); // list | thread | contacts
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [presence, setPresence] = useState({});
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [search, setSearch] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiTab, setEmojiTab] = useState('recent');
  const [recentEmojis, setRecentEmojis] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('gab_chat_recent_emojis') || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch { return []; }
  });

  const draftRef = useRef(null);

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const lastMsgTimeRef = useRef(null);
  const typingTimerRef = useRef(null);

  // Show only for internal collaborators (agent/admin/super_admin)
  const enabled = isAuthenticated && user && CHAT_ROLES.includes(user.role);

  // -------- Data fetching --------
  const fetchUnread = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await api.get('/chat/unread-count');
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ }
  }, [enabled]);

  const fetchThreads = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await api.get('/chat/threads');
      setThreads(r.data || []);
    } catch { /* silent */ }
  }, [enabled]);

  const fetchPresence = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await api.get('/chat/presence');
      const map = {};
      (r.data || []).forEach((p) => { map[p.user_id || p.id] = p.online; });
      setPresence(map);
    } catch { /* silent */ }
  }, [enabled]);

  const fetchContacts = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await api.get('/chat/contacts');
      setContacts(r.data || []);
    } catch { /* silent */ }
  }, [enabled]);

  const fetchMessages = useCallback(async (threadId, incremental = false) => {
    if (!enabled || !threadId) return;
    try {
      const params = {};
      if (incremental && lastMsgTimeRef.current) params.after = lastMsgTimeRef.current;
      const r = await api.get(`/chat/threads/${threadId}/messages`, { params });
      const list = r.data || [];
      if (incremental) {
        if (list.length > 0) {
          setMessages((prev) => [...prev, ...list]);
          lastMsgTimeRef.current = list[list.length - 1].created_at;
        }
      } else {
        setMessages(list);
        if (list.length) lastMsgTimeRef.current = list[list.length - 1].created_at;
      }
      // mark read
      await api.post(`/chat/threads/${threadId}/read`).catch(() => {});
    } catch { /* silent */ }
  }, [enabled]);

  const fetchTyping = useCallback(async (threadId) => {
    if (!enabled || !threadId) return;
    try {
      const r = await api.get(`/chat/threads/${threadId}/typing`);
      setTypingUsers(r.data || []);
    } catch { /* silent */ }
  }, [enabled]);

  // -------- Presence heartbeat (light) --------
  useEffect(() => {
    if (!enabled) return undefined;
    const ping = () => { api.post('/chat/presence/ping').catch(() => {}); };
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, [enabled]);

  // -------- Global unread polling --------
  useEffect(() => {
    if (!enabled) return undefined;
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => clearInterval(t);
  }, [enabled, fetchUnread]);

  // -------- Open panel effects --------
  useEffect(() => {
    if (!open || !enabled) return;
    if (view === 'list') {
      fetchThreads();
      fetchPresence();
    } else if (view === 'contacts') {
      fetchContacts();
    }
  }, [open, view, enabled, fetchThreads, fetchPresence, fetchContacts]);

  // -------- Poll threads list while panel open on list view --------
  useEffect(() => {
    if (!open || view !== 'list' || !enabled) return undefined;
    const t = setInterval(() => { fetchThreads(); fetchUnread(); }, 8000);
    return () => clearInterval(t);
  }, [open, view, enabled, fetchThreads, fetchUnread]);

  // -------- Poll active thread messages --------
  useEffect(() => {
    if (!open || view !== 'thread' || !activeThread || !enabled) return undefined;
    const t = setInterval(() => {
      fetchMessages(activeThread.id, true);
      fetchTyping(activeThread.id);
    }, 4000);
    return () => clearInterval(t);
  }, [open, view, activeThread, enabled, fetchMessages, fetchTyping]);

  // -------- Auto scroll bottom on new messages --------
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, activeThread?.id]);

  // -------- Handlers --------
  const openThread = async (thread) => {
    setActiveThread(thread);
    setView('thread');
    setMessages([]);
    lastMsgTimeRef.current = null;
    setLoading(true);
    await fetchMessages(thread.id, false);
    setLoading(false);
    fetchTyping(thread.id);
  };

  const openDM = async (contact) => {
    setLoading(true);
    try {
      const r = await api.post('/chat/threads/dm', { user_id: contact.id });
      openThread(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible d\'ouvrir la conversation');
    } finally {
      setLoading(false);
    }
  };

  const handlePickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Image invalide'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error('Image trop lourde (>8 Mo)'); return; }
    setImage(f);
    const url = URL.createObjectURL(f);
    setImagePreview(url);
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const sendMessage = async () => {
    if (!activeThread || sending) return;
    const body = draft.trim();
    if (!body && !image) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('body', body);
      if (image) fd.append('image', image);
      const r = await api.post(`/chat/threads/${activeThread.id}/messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages((prev) => [...prev, r.data]);
      lastMsgTimeRef.current = r.data.created_at;
      setDraft('');
      clearImage();
      // refresh thread list preview
      fetchThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Envoi impossible');
    } finally {
      setSending(false);
    }
  };

  const onDraftChange = (e) => {
    setDraft(e.target.value);
    // typing indicator throttle
    if (!activeThread) return;
    if (typingTimerRef.current) return;
    api.post(`/chat/threads/${activeThread.id}/typing`).catch(() => {});
    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 2000);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const insertEmoji = (emoji) => {
    const el = draftRef.current;
    if (el) {
      const start = el.selectionStart ?? draft.length;
      const end = el.selectionEnd ?? draft.length;
      const next = draft.slice(0, start) + emoji + draft.slice(end);
      setDraft(next);
      // reposition caret after render
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + emoji.length;
        try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
      });
    } else {
      setDraft((d) => d + emoji);
    }
    // update recents (unique + max 20)
    setRecentEmojis((prev) => {
      const next = [emoji, ...prev.filter((e) => e !== emoji)].slice(0, 20);
      try { localStorage.setItem('gab_chat_recent_emojis', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  // Close emoji picker on click outside
  useEffect(() => {
    if (!emojiOpen) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest('[data-emoji-panel]') && !e.target.closest('[data-testid="floating-chat-emoji-btn"]')) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [emojiOpen]);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => (t.display_name || '').toLowerCase().includes(q));
  }, [threads, search]);

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q));
  }, [contacts, contactSearch]);

  if (!enabled) return null;

  // ---- Floating button (closed) ----
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setMinimized(false); }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition transform hover:scale-105 active:scale-95"
        style={{ background: `linear-gradient(135deg, ${PALETTE.forest} 0%, ${PALETTE.forestSoft} 100%)` }}
        data-testid="floating-chat-toggle"
        aria-label="Ouvrir la messagerie"
      >
        <MessageCircle className="w-6 h-6" strokeWidth={2.2} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#B85C38] text-white text-[11px] font-bold flex items-center justify-center border-2 border-white shadow" data-testid="floating-chat-unread-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  }

  // ---- Minimized (compact bar at bottom-right) ----
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-6 right-6 z-50 px-4 py-3 rounded-full shadow-2xl flex items-center gap-2 text-white transition hover:scale-105"
        style={{ background: `linear-gradient(135deg, ${PALETTE.forest} 0%, ${PALETTE.forestSoft} 100%)` }}
        data-testid="floating-chat-restore"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-semibold">Messagerie</span>
        {unread > 0 && (
          <span className="ml-1 min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#B85C38] text-[11px] font-bold flex items-center justify-center border-2 border-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="fixed z-50 bottom-4 right-4 md:bottom-6 md:right-6 flex flex-col rounded-3xl shadow-2xl overflow-hidden border border-[#B85C38]/20 bg-white"
      style={{
        width: 'min(380px, calc(100vw - 24px))',
        height: 'min(600px, calc(100vh - 40px))',
      }}
      data-testid="floating-chat-panel"
    >
      {/* Header */}
      <div
        className="relative flex items-center gap-3 px-4 py-3 text-white"
        style={{ background: `linear-gradient(135deg, ${PALETTE.forest} 0%, ${PALETTE.forestSoft} 100%)` }}
      >
        {view !== 'list' && (
          <button
            onClick={() => { setView('list'); setActiveThread(null); setTypingUsers([]); }}
            className="p-1.5 rounded-lg hover:bg-white/10 transition"
            data-testid="floating-chat-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          {view === 'thread' && activeThread ? (
            <div className="flex items-center gap-2 min-w-0">
              {activeThread.kind === 'agency' ? (
                <div className="w-9 h-9 rounded-full bg-[#B85C38] flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-white" />
                </div>
              ) : (
                <Avatar name={activeThread.display_name} size={36} online={presence[activeThread.other_user_id]} />
              )}
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">{activeThread.display_name || 'Conversation'}</p>
                {typingUsers.length > 0 ? (
                  <p className="text-[10px] text-[#F5EFE1]/90 italic">{typingUsers.map((u) => u.name).join(', ')} écrit…</p>
                ) : activeThread.kind === 'dm' ? (
                  <p className="text-[10px] text-[#F5EFE1]/80">
                    <Circle className={`w-1.5 h-1.5 inline mr-1 ${presence[activeThread.other_user_id] ? 'text-emerald-400 fill-emerald-400' : 'text-gray-400 fill-gray-400'}`} />
                    {presence[activeThread.other_user_id] ? 'En ligne' : 'Hors ligne'}
                  </p>
                ) : (
                  <p className="text-[10px] text-[#F5EFE1]/80">Discussion d&apos;équipe</p>
                )}
              </div>
            </div>
          ) : view === 'contacts' ? (
            <div>
              <p className="font-bold text-sm">Nouveau chat</p>
              <p className="text-[10px] text-[#F5EFE1]/80">Choisissez un collaborateur</p>
            </div>
          ) : (
            <div>
              <p className="font-bold text-sm flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Messagerie
              </p>
              <p className="text-[10px] text-[#F5EFE1]/80">{threads.length} conversation(s)</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(true)} className="p-1.5 rounded-lg hover:bg-white/10 transition" title="Réduire" data-testid="floating-chat-minimize">
            <Minimize2 className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition" title="Fermer" data-testid="floating-chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      {view === 'list' && (
        <div className="flex-1 flex flex-col min-h-0" style={{ background: PALETTE.sand }}>
          <div className="p-3 bg-white border-b border-[#B85C38]/10 flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full pl-8 pr-3 py-2 rounded-full text-sm bg-[#F5EFE1] focus:bg-white focus:ring-1 focus:ring-[#B85C38] outline-none border border-transparent"
                data-testid="floating-chat-search"
              />
            </div>
            <button
              onClick={() => setView('contacts')}
              className="p-2 rounded-full bg-[#B85C38] text-white hover:bg-[#a04d2f] transition"
              title="Nouveau chat"
              data-testid="floating-chat-new-btn"
            >
              <Users className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredThreads.length === 0 ? (
              <div className="p-8 text-center text-xs text-gray-500">Aucune conversation.</div>
            ) : filteredThreads.map((t) => (
              <button
                key={t.id}
                onClick={() => openThread(t)}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#F5EFE1]/60 border-b border-gray-100 text-left transition"
                data-testid={`floating-chat-thread-${t.id}`}
              >
                {t.kind === 'agency' ? (
                  <div className="w-11 h-11 rounded-full bg-[#B85C38] flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <Avatar name={t.display_name} size={44} online={presence[t.other_user_id]} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm text-[#2B3A2C] truncate">{t.display_name || 'Conversation'}</p>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtTime(t.last_at)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-xs text-gray-500 truncate">{t.last_message_preview || 'Aucun message'}</p>
                    {t.unread_count > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#B85C38] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {t.unread_count > 99 ? '99+' : t.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {view === 'contacts' && (
        <div className="flex-1 flex flex-col min-h-0" style={{ background: PALETTE.sand }}>
          <div className="p-3 bg-white border-b border-[#B85C38]/10">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="Rechercher un collaborateur…"
                className="w-full pl-8 pr-3 py-2 rounded-full text-sm bg-[#F5EFE1] focus:bg-white focus:ring-1 focus:ring-[#B85C38] outline-none border border-transparent"
                data-testid="floating-chat-contact-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="p-8 text-center text-xs text-gray-500">Aucun collaborateur trouvé.</div>
            ) : filteredContacts.map((c) => (
              <button
                key={c.id}
                onClick={() => openDM(c)}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#F5EFE1]/60 border-b border-gray-100 text-left transition"
                data-testid={`floating-chat-contact-${c.id}`}
              >
                <Avatar name={c.name} size={40} online={presence[c.id]} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#2B3A2C] truncate">{c.name}</p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {c.role === 'admin' ? 'Administrateur' : c.role === 'super_admin' ? 'Super-Admin' : 'Agent'} · {c.employee_id || c.email}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-300 rotate-[-90deg]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {view === 'thread' && activeThread && (
        <>
          {/* Messages area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5"
            style={{
              background: PALETTE.sand,
              backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22><g fill=%22none%22 stroke=%22%23B85C38%22 stroke-opacity=%220.06%22 stroke-width=%221%22><circle cx=%2250%22 cy=%2250%22 r=%2240%22/><circle cx=%2250%22 cy=%2250%22 r=%2226%22/></g></svg>')",
              backgroundSize: '160px 160px',
            }}
            data-testid="floating-chat-messages"
          >
            {loading && messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-xs text-gray-500 py-8">
                Aucun message. Écrivez le premier !
              </div>
            ) : messages.map((m, i) => {
              const mine = m.sender_id === user.id;
              const showSender = activeThread.kind === 'agency' && !mine &&
                (i === 0 || messages[i - 1].sender_id !== m.sender_id);
              const isSystem = m.system;
              if (isSystem) {
                return (
                  <div key={m.id || i} className="flex justify-center my-2">
                    <span className="text-[10px] text-gray-500 bg-white/70 backdrop-blur px-3 py-1 rounded-full border border-gray-200">
                      {m.body}
                    </span>
                  </div>
                );
              }
              return (
                <div key={m.id || i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[78%] rounded-2xl px-3 py-1.5 shadow-sm ${
                    mine ? 'rounded-tr-sm' : 'rounded-tl-sm'
                  }`} style={{ background: mine ? PALETTE.bubbleMine : PALETTE.bubbleTheirs }}>
                    {showSender && (
                      <p className="text-[10px] font-bold text-[#B85C38] mb-0.5">{m.sender_name}</p>
                    )}
                    {m.image_url && (
                      <img src={m.image_url.startsWith('http') ? m.image_url : `${process.env.REACT_APP_BACKEND_URL}${m.image_url}`}
                           alt="pièce jointe"
                           className="rounded-lg max-w-full max-h-52 object-cover mb-1 cursor-pointer"
                           onClick={() => window.open(m.image_url.startsWith('http') ? m.image_url : `${process.env.REACT_APP_BACKEND_URL}${m.image_url}`, '_blank')}
                      />
                    )}
                    {m.body && <p className="text-sm text-[#2B3A2C] whitespace-pre-wrap break-words">{m.body}</p>}
                    <div className={`flex items-center gap-1 mt-0.5 ${mine ? 'justify-end' : 'justify-end'}`}>
                      <span className="text-[9px] text-gray-500">{fmtTime(m.created_at)}</span>
                      {mine && activeThread.kind === 'dm' && (
                        m.read_by_other
                          ? <CheckCheck className="w-3 h-3 text-sky-500" />
                          : <Check className="w-3 h-3 text-gray-400" />
                      )}
                      {mine && activeThread.kind === 'agency' && m.read_by_count > 0 && (
                        <span className="text-[9px] text-sky-500 font-semibold">✓ {m.read_by_count}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Image preview */}
          {imagePreview && (
            <div className="px-3 py-2 border-t border-gray-200 bg-white flex items-center gap-2">
              <img src={imagePreview} alt="preview" className="h-14 w-14 rounded-lg object-cover" />
              <div className="flex-1 text-xs text-gray-600">Image prête à envoyer</div>
              <button onClick={clearImage} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
          )}

          {/* Input */}
          <div className="relative p-2.5 bg-white border-t border-[#B85C38]/10 flex items-end gap-2">
            {/* Emoji picker floating above the input */}
            {emojiOpen && (
              <div
                data-emoji-panel
                className="absolute bottom-full left-2 right-2 mb-2 bg-white rounded-2xl shadow-2xl border border-[#B85C38]/20 overflow-hidden"
                data-testid="floating-chat-emoji-panel"
              >
                <div className="flex items-center gap-0.5 px-1.5 py-1.5 border-b border-gray-100 bg-[#F5EFE1]/70 overflow-x-auto">
                  {EMOJI_CATEGORIES.map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => setEmojiTab(cat.key)}
                      className={`px-2 py-1 rounded-lg text-base leading-none transition ${
                        emojiTab === cat.key ? 'bg-white shadow-sm ring-1 ring-[#B85C38]/25' : 'hover:bg-white/60'
                      }`}
                      title={cat.label}
                      data-testid={`floating-chat-emoji-cat-${cat.key}`}
                    >
                      {cat.icon}
                    </button>
                  ))}
                </div>
                <div className="max-h-52 overflow-y-auto p-2 grid grid-cols-8 gap-1">
                  {(emojiTab === 'recent'
                    ? (recentEmojis.length ? recentEmojis : EMOJI_CATEGORIES[0].emojis)
                    : EMOJI_CATEGORIES.find((c) => c.key === emojiTab)?.emojis || []
                  ).map((emo, idx) => (
                    <button
                      key={`${emojiTab}-${idx}`}
                      onClick={() => insertEmoji(emo)}
                      className="text-xl leading-none p-1.5 rounded-lg hover:bg-[#F5EFE1] active:scale-90 transition"
                      data-testid={`floating-chat-emoji-${emo}`}
                    >
                      {emo}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handlePickImage} data-testid="floating-chat-file-input" />
            <button
              onClick={() => { setEmojiOpen((v) => !v); }}
              className={`p-2 rounded-full transition ${emojiOpen ? 'bg-[#F5EFE1] text-[#B85C38]' : 'text-gray-500 hover:bg-[#F5EFE1]'}`}
              title="Insérer un emoji"
              data-testid="floating-chat-emoji-btn"
            >
              <Smile className="w-5 h-5" />
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              className="p-2 rounded-full text-gray-500 hover:bg-[#F5EFE1] transition"
              title="Joindre une image"
              data-testid="floating-chat-attach-btn"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
            <textarea
              ref={draftRef}
              value={draft}
              onChange={onDraftChange}
              onKeyDown={onKeyDown}
              placeholder="Écrire un message…"
              rows={1}
              className="flex-1 resize-none max-h-28 text-sm bg-[#F5EFE1] rounded-2xl px-3 py-2 focus:bg-white focus:ring-1 focus:ring-[#B85C38] outline-none border border-transparent"
              data-testid="floating-chat-draft"
            />
            <button
              onClick={sendMessage}
              disabled={sending || (!draft.trim() && !image)}
              className="p-2.5 rounded-full text-white shadow-md transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: PALETTE.copper }}
              data-testid="floating-chat-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FloatingChat;
